import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ComponentsIcon from 'images/v5/icons/menu/components.svg';
import Label from 'layout/atoms/Label';
import Modal from 'layout/atoms/Modal/Modal';
import FormSaveActions from 'layout/molecules/FormSaveActions/FormSaveActions';
import ComponentEditor from 'organisms/ComponentEditor';
import { fetchComponentDetails } from 'store/componentDetailsSlice';
import { addItem, increaseCategoryCounter, selectCategoryById } from 'store/componentsSlice';
import { closeModal, openSidebar, selectModalByName } from 'store/layoutSlice';
import { getComponentCategoryName } from 'utils/componentCategories';
import useComponentValidation from '../useComponentValidation';
import useProductEditorView from '../useProductEditorView';
function getProductData(productForm) {
    var _a, _b, _c, _d, _e, _f, _g;
    return {
        productDescription: productForm.productDescription,
        supplierId: (_a = productForm.supplierObj) === null || _a === void 0 ? void 0 : _a.id,
        name: productForm.name,
        categoryId: (_b = productForm.categoryObj) === null || _b === void 0 ? void 0 : _b.id,
        width: productForm.width,
        composition: productForm.composition,
        componentSize: productForm.componentSize,
        zipperType: productForm.zipperType,
        grammage: productForm.grammage,
        pictures: productForm.pictures.map((file) => file.fileData),
        producerCode: productForm.producerCode,
        underSliderType: productForm.underSliderType,
        upperSliderType: productForm.upperSliderType,
        yardage: productForm.yardage,
        properName: productForm === null || productForm === void 0 ? void 0 : productForm.properName,
        orderOperatorId: (_c = productForm === null || productForm === void 0 ? void 0 : productForm.orderOperatorObj) === null || _c === void 0 ? void 0 : _c.id,
        productionTimeDays: (productForm === null || productForm === void 0 ? void 0 : productForm.productionTimeDays)
            ? parseInt(productForm.productionTimeDays, 10)
            : null,
        continentId: (_d = productForm.continentObj) === null || _d === void 0 ? void 0 : _d.id,
        countryId: (_e = productForm.countryObj) === null || _e === void 0 ? void 0 : _e.id,
        eventId: (_f = productForm.eventObj) === null || _f === void 0 ? void 0 : _f.id,
        mainCategoryId: (_g = productForm.mainCategoryObj) === null || _g === void 0 ? void 0 : _g.id,
        functionIds: (productForm.functions || []).map((func) => func.id),
        sustainabilityIds: (productForm.sustainabilities || []).map((sustainability) => sustainability.id),
        certificateIds: (productForm.certificates || []).map((certificate) => certificate.id),
    };
}
function ComponentCreator() {
    var _a;
    const { i18n } = useTranslation();
    const dispatch = useDispatch();
    const componentCreatorModal = useSelector(selectModalByName('componentCreator'));
    const predefinedCategory = ((_a = componentCreatorModal === null || componentCreatorModal === void 0 ? void 0 : componentCreatorModal.data) === null || _a === void 0 ? void 0 : _a.predefinedCategory) || null;
    const predefinedCategoryData = useSelector((state) => predefinedCategory && selectCategoryById(state, predefinedCategory));
    const [productForm, setProductForm] = useState({
        pictures: [],
        productDescription: '',
        supplier: '',
        supplierObj: {},
        continent: '',
        continentObj: {},
        country: '',
        countryObj: {},
        event: '',
        eventObj: {},
        mainCategory: '',
        mainCategoryObj: {},
        name: '',
        category: predefinedCategoryData
            ? getComponentCategoryName(i18n, predefinedCategoryData.typeLabelPL, predefinedCategoryData.typeLabelEN)
            : '',
        categoryObj: predefinedCategoryData || {},
        width: '',
        composition: '',
        componentSize: '',
        zipperType: '',
        grammage: '',
        producerCode: '',
        underSliderType: '',
        upperSliderType: '',
        yardage: '',
        orderOperator: '',
        orderOperatorObj: {},
        productionTimeDays: '',
        functions: [],
        sustainabilities: [],
        certificates: [],
    });
    const { t } = useTranslation('specification');
    const { onProductAdd, addProductLoading } = useProductEditorView();
    const { errors, isValid, pendingFields, markTouched, validateAll } = useComponentValidation(productForm, t);
    const handleCancelClick = () => {
        dispatch(closeModal({ name: 'componentCreator' }));
    };
    const handleFormChange = ({ target: { name, text, value }, }) => {
        if (name === 'category')
            markTouched('categoryObj');
        if (name === 'orderOperator')
            markTouched('orderOperatorObj');
        if (name === 'supplier')
            markTouched('supplierObj');
        if (name === 'functions' || name === 'sustainabilities' || name === 'certificates') {
            setProductForm((prdForm) => (Object.assign(Object.assign({}, prdForm), { [name]: value })));
            return;
        }
        if (text) {
            if (name === 'category') {
                setProductForm((prdForm) => (Object.assign(Object.assign({}, prdForm), { supplier: '', supplierObj: {}, continent: '', continentObj: {}, country: '', countryObj: {}, event: '', eventObj: {}, mainCategory: '', mainCategoryObj: {}, width: '', composition: '', componentSize: '', zipperType: '', grammage: '', producerCode: '', underSliderType: '', upperSliderType: '', yardage: '', [name]: text, [`${name}Obj`]: value, properName: '', orderOperator: '', orderOperatorObj: {}, functions: [], sustainabilities: [], certificates: [] })));
                if (value === null || value === void 0 ? void 0 : value.nameAutoCalculationMethod) {
                    setProductForm((prdForm) => (Object.assign(Object.assign({}, prdForm), { name: '' })));
                }
            }
            else {
                setProductForm((prdForm) => (Object.assign(Object.assign({}, prdForm), { supplier: name === 'category' ? '' : prdForm === null || prdForm === void 0 ? void 0 : prdForm.supplier, supplierObj: name === 'category' ? {} : prdForm === null || prdForm === void 0 ? void 0 : prdForm.supplierObj, [name]: text, [`${name}Obj`]: value })));
            }
        }
        else {
            setProductForm((prdForm) => (Object.assign(Object.assign({}, prdForm), { [name]: value })));
        }
    };
    const handleSaveProduct = () => {
        if (!validateAll())
            return;
        onProductAdd(getProductData(productForm)).then((response) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
            dispatch(addItem({
                id: (_c = (_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.addProduct) === null || _b === void 0 ? void 0 : _b.product) === null || _c === void 0 ? void 0 : _c.id,
                data: (_e = (_d = response.data) === null || _d === void 0 ? void 0 : _d.addProduct) === null || _e === void 0 ? void 0 : _e.product,
            }));
            dispatch(increaseCategoryCounter({ id: (_j = (_h = (_g = (_f = response.data) === null || _f === void 0 ? void 0 : _f.addProduct) === null || _g === void 0 ? void 0 : _g.product) === null || _h === void 0 ? void 0 : _h.category) === null || _j === void 0 ? void 0 : _j.id }));
            dispatch(fetchComponentDetails({ componentId: (_m = (_l = (_k = response.data) === null || _k === void 0 ? void 0 : _k.addProduct) === null || _l === void 0 ? void 0 : _l.product) === null || _m === void 0 ? void 0 : _m.id }));
            dispatch(closeModal({ name: 'componentCreator' }));
            dispatch(openSidebar({ name: 'componentPreview', id: (_q = (_p = (_o = response.data) === null || _o === void 0 ? void 0 : _o.addProduct) === null || _p === void 0 ? void 0 : _p.product) === null || _q === void 0 ? void 0 : _q.id }));
        });
    };
    return (React.createElement(Modal, { size: "l", name: "componentCreator", header: React.createElement(Label, null,
            React.createElement(ComponentsIcon, null),
            t('common:new_component')), footer: React.createElement(FormSaveActions, { isValid: isValid, isLoading: addProductLoading, pendingFields: pendingFields, onSave: handleSaveProduct, onCancel: handleCancelClick, validateAll: validateAll, saveLabel: t('common:save_component') }) },
        React.createElement(ComponentEditor, { productForm: productForm, onFormChange: handleFormChange, isEdition: false, errors: errors, onFieldBlur: markTouched })));
}
export default memo(ComponentCreator);
